import React from "react"
import { Toll } from "./RouteView"
import { cn } from "@/lib/utils"
import { getCountryFlag, getCountryName } from "./mapUtils"
import { useTranslation } from "react-i18next"
import { getIntlLocale } from "@/lib/i18n/format"

type TollsInformationProps = {
  tolls: Toll[] | undefined
  isSharedMap?: boolean
  className?: string
}

// Currency formatting function
const formatCurrency = (amount: number, currency: string): string => {
  const formatter = new Intl.NumberFormat(getIntlLocale(undefined), {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
  return formatter.format(amount)
}

const TollsInformation = ({
  tolls,
  isSharedMap = false,
  className,
}: TollsInformationProps) => {
  const { t } = useTranslation()
  if (!tolls || tolls.length === 0) return null

  // Group tolls by country and currency
  const tollsByCountry = tolls.reduce<Record<string, Record<string, number>>>(
    (acc, toll) => {
      if (!acc[toll.country_code]) {
        acc[toll.country_code] = {}
      }

      if (!acc[toll.country_code][toll.currency]) {
        acc[toll.country_code][toll.currency] = 0
      }

      acc[toll.country_code][toll.currency] += toll.price
      return acc
    },
    {},
  )

  // Calculate total by currency
  const totalByCurrency = tolls.reduce<Record<string, number>>((acc, toll) => {
    if (!acc[toll.currency]) {
      acc[toll.currency] = 0
    }
    acc[toll.currency] += toll.price
    return acc
  }, {})

  return (
    <div
      className={cn(
        isSharedMap
          ? "w-full border-b bg-white p-4 dark:bg-gray-800"
          : "mx-2 mb-2 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800",
        className,
      )}
    >
      <div>
        <h2 className="mb-2 font-medium text-gray-900 dark:text-white">
          {t("maps.tollsInformation.title")}
        </h2>

        <div className="my-1 flex flex-col gap-1">
          {Object.entries(tollsByCountry)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([countryCode, currencyMap]) => (
              <div
                key={countryCode}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <span className="text-base">
                    {getCountryFlag(countryCode)}
                  </span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {getCountryName(countryCode)}
                  </span>
                </div>
                <div className="text-right">
                  {Object.entries(currencyMap).map(([currency, amount]) => (
                    <div
                      key={`${countryCode}-${currency}`}
                      className="font-semibold text-gray-900 dark:text-white"
                    >
                      {formatCurrency(amount, currency)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>

        <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-900 dark:text-white">
              {t("maps.tollsInformation.total")}
            </span>
            <div className="font-medium text-gray-900 dark:text-white">
              {Object.entries(totalByCurrency)
                .map(([currency, amount]) => formatCurrency(amount, currency))
                .join(" + ")}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TollsInformation
